/* eslint-disable react/require-default-props */
import { Slot } from '@radix-ui/react-slot'
import { clsx } from 'clsx'
import type { ComponentProps, ReactNode } from 'react'

type OuterProps = ComponentProps<'div'>

function Outer(props: OuterProps): ReactNode {
  const { children, className, ...rest } = props
  return (
    <div className={clsx('box', className)} {...rest}>
      {children}
    </div>
  )
}
Outer.displayName = 'Box.Outer'

type TitleProps = ComponentProps<'h3'> & {
  asChild?: boolean
  isDanger?: boolean
}

function Title(props: TitleProps) {
  const { asChild, children, className, isDanger, ...rest } = props
  const Component = asChild ? Slot : 'h3'
  return (
    <Component
      {...(rest as Omit<ComponentProps<typeof Component>, 'ref'>)}
      className={clsx(
        'box__title',
        {
          'text-danger': isDanger,
        },
        className,
      )}
    >
      {children}
    </Component>
  )
}
Title.displayName = `Box.Title`

type DescriptionProps = ComponentProps<'div'>
function Description(props: DescriptionProps) {
  const { children, className, ...rest } = props
  return (
    <div {...rest} className={clsx('box__description', className)}>
      {children}
    </div>
  )
}
Description.displayName = `Box.Description`

interface HeaderProps {
  addon?: ReactNode
  addonHasTabs?: boolean
  children?: ReactNode
  className?: string
  description?: ReactNode
  hasDashedBorderBottom?: boolean
  icon?: string
  isDanger?: boolean
  isWide?: boolean
}

function Header(props: HeaderProps) {
  const {
    addon,
    addonHasTabs,
    children,
    className,
    description,
    hasDashedBorderBottom,
    icon,
    isDanger,
    isWide,
  } = props
  return (
    <div
      className={clsx(
        `box__header`,
        {
          'box__header--dashed': hasDashedBorderBottom,
        },
        className,
      )}
    >
      {icon && <img className="box__icon" src={icon} alt="" />}

      <div className={clsx('box__header-inner', { 'box__header-inner--wide': isWide })}>
        <Title isDanger={isDanger}>{children}</Title>
        {description && <Description>{description}</Description>}
      </div>

      {addon && (
        <div
          className={clsx('box__header-addon', {
            'box__header-addon--tabs': addonHasTabs,
          })}
        >
          {addon}
        </div>
      )}
    </div>
  )
}
Header.displayName = 'Box.Header'

type ContentProps = ComponentProps<'div'> & {
  asChild?: boolean
  columns?: boolean
}

function Content(props: ContentProps) {
  const { asChild, className, children, columns } = props
  const Component = asChild ? Slot : 'div'
  return (
    <Component className={clsx(`box__content`, { 'box__content--columns': columns }, className)}>
      {children}
    </Component>
  )
}
Content.displayName = 'Box.Content'

interface SectionProps {
  hasTable?: boolean
  hasCodeBlock?: boolean
  isSeparated?: boolean
  className?: string
  children?: ReactNode
}

function Section(props: SectionProps) {
  const { hasTable, hasCodeBlock, isSeparated, className, children } = props

  const sectionClassName = clsx('box__section', className, {
    'box__section--has-table': hasTable,
    'box__section--has-code-block': hasCodeBlock,
    'box__section--separated': isSeparated,
  })

  return <div className={sectionClassName}>{children}</div>
}

interface FooterProps {
  children?: ReactNode
}

function Footer(props: FooterProps) {
  const { children } = props
  return <div className="box__footer">{children}</div>
}
Footer.displayName = 'Box.Footer'

export default Outer

export { Content, Description, Footer, Header, Outer, Section, Title }
